import { useMemo } from "react";
import { Box, Flex, Grid, Heading, Text } from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// EG-38 — Dashboard "Maturidade ESG por pilar" (painel admin). Reusa o dado do
// diagnóstico (`survey/diagnostic/all/result`): nível de maturidade 0–5 por pilar.

const INK = "#292B30";
const INK_MUTED = "#5C6066";
const MATURITY = "#598F3C";
const SURFACE = "#FFFFFF";
const MAX_LEVEL = 5;

function MaturityTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <Box bg={SURFACE} border="1px solid #E2E8F0" borderRadius="8px" p="10px" boxShadow="sm">
      <Text fontSize="13px" fontWeight={600} color={INK} mb="2px">
        {row.pillar}
      </Text>
      <Text fontSize="12px" color={INK_MUTED}>
        Nível {row.nivel}
        {row.nivelNome ? ` · ${row.nivelNome}` : ""}
      </Text>
    </Box>
  );
}

function DiagnosisSection({ diagnosis }) {
  const rows = useMemo(
    () =>
      (diagnosis.pillars || []).map((p) => ({
        pillar: p.name_pillar,
        nivel: Number(p.maturityLevel ?? 0),
        nivelNome: p.maturityLevelName || "",
      })),
    [diagnosis.pillars]
  );

  if (rows.length === 0) return null;

  return (
    <Box bg={SURFACE} borderRadius="12px" p={["20px", "28px"]} mb="24px">
      <Heading size="md" color={INK} lineHeight="1.2" mb="4px">
        {diagnosis.survey?.description}
      </Heading>
      <Text fontSize="13px" color={INK_MUTED} mb="20px">
        Nível de maturidade (0–5)
      </Text>

      <Grid templateColumns={{ base: "1fr", xl: "1.4fr 1fr" }} gap="24px">
        <Box>
          <Text fontSize="14px" fontWeight={600} color={INK} mb="12px">
            Maturidade por pilar
          </Text>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={rows} margin={{ top: 16, right: 8, bottom: 8, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
              <XAxis
                dataKey="pillar"
                tick={{ fontSize: 12, fill: INK_MUTED }}
                tickLine={false}
                axisLine={{ stroke: "#E2E8F0" }}
              />
              <YAxis
                domain={[0, MAX_LEVEL]}
                ticks={[0, 1, 2, 3, 4, 5]}
                tick={{ fontSize: 12, fill: INK_MUTED }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<MaturityTooltip />} cursor={{ fill: "rgba(89,143,60,0.08)" }} />
              <Bar dataKey="nivel" fill={MATURITY} radius={[4, 4, 0, 0]} maxBarSize={48}>
                <LabelList dataKey="nivel" position="top" fontSize={12} fill={INK_MUTED} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Box>
          <Text fontSize="14px" fontWeight={600} color={INK} mb="12px">
            Perfil de maturidade
          </Text>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={rows} outerRadius="72%">
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 11, fill: INK_MUTED }} />
              <PolarRadiusAxis domain={[0, MAX_LEVEL]} tick={{ fontSize: 10, fill: INK_MUTED }} />
              <Radar name="Nível" dataKey="nivel" stroke={MATURITY} fill={MATURITY} fillOpacity={0.28} />
              <Tooltip content={<MaturityTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
    </Box>
  );
}

export default function MaturityDashboard({ diagnoses }) {
  const withPillars = (diagnoses || []).filter((d) => (d.pillars?.length ?? 0) > 0);

  if (withPillars.length === 0) {
    return (
      <Flex
        bg={SURFACE}
        borderRadius="12px"
        p={["32px", "48px"]}
        direction="column"
        align="center"
        textAlign="center"
        gap="8px"
      >
        <Heading size="sm" color={INK}>
          Diagnóstico ainda não disponível
        </Heading>
        <Text color={INK_MUTED} fontSize="14px" maxW="420px">
          Quando o diagnóstico desta empresa for concluído, o nível de maturidade por pilar aparece
          aqui.
        </Text>
      </Flex>
    );
  }

  return (
    <Box>
      {withPillars.map((diagnosis, index) => (
        <DiagnosisSection key={diagnosis.survey?.id_survey ?? index} diagnosis={diagnosis} />
      ))}
    </Box>
  );
}
